/**
 * WebSocket Service for real-time updates
 * Handles connection, reconnection, and event handling
 */

type WebSocketEventType = 
  | 'device.status'
  | 'sensor.reading'
  | 'valve.action'
  | 'irrigation.event'
  | 'schedule.triggered'
  | 'trigger.fired'
  | 'connection.status';

interface WebSocketMessage {
  type: WebSocketEventType;
  payload: unknown;
  timestamp: string;
}

interface DeviceStatusPayload {
  deviceId: string;
  deviceType: 'controller' | 'sensor';
  status: 'online' | 'offline' | 'warning';
  battery?: number;
  lastSeen: string;
}

interface SensorReadingPayload {
  sensorId: string;
  temperature: number;
  humidity: number;
  soilMoisture?: number;
  soilEC?: number;
  timestamp: string;
}

interface ValveActionPayload {
  controllerId: string;
  valveChannel: number;
  action: 'open' | 'close';
  triggeredBy: 'manual' | 'schedule' | 'trigger';
  timestamp: string;
}

interface IrrigationEventPayload {
  id: string;
  gardenId: string;
  zoneId: string;
  controllerId: string;
  valveChannel: number;
  eventType: 'start' | 'stop' | 'pause' | 'resume';
  source: 'manual' | 'schedule' | 'trigger';
  timestamp: string;
}

type EventCallback<T = unknown> = (payload: T) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private url: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 2; // Reducido: backend aún no tiene WebSocket
  private reconnectDelay = 1000;
  private eventListeners: Map<WebSocketEventType, Set<EventCallback>> = new Map();
  private isConnecting = false;
  private shouldReconnect = true;
  private heartbeatInterval: number | null = null;
  private token: string | null = null;

  constructor() {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
    // Convert http(s) to ws(s)
    // API URL: https://irrigation.swat-id.com/api → WS URL: wss://irrigation.swat-id.com/api/v1/ws
    // Backend WebSocket está en /v1/ws, NGINX lo expone como /api/v1/ws
    // Manejar tanto http como https correctamente
    let wsUrl: string;
    if (baseUrl.startsWith('https://')) {
      wsUrl = baseUrl.replace('https://', 'wss://');
    } else if (baseUrl.startsWith('http://')) {
      wsUrl = baseUrl.replace('http://', 'ws://');
    } else {
      // Si no tiene protocolo, asumir https en producción
      wsUrl = (baseUrl.includes('localhost') ? 'ws://' : 'wss://') + baseUrl;
    }
    // Añadir /v1/ws al final (baseUrl ya incluye /api si es necesario)
    this.url = wsUrl + '/v1/ws';
  }

  /**
   * Connect to WebSocket server
   */
  connect(token?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      if (this.isConnecting) {
        reject(new Error('Connection already in progress'));
        return;
      }

      this.isConnecting = true;
      this.shouldReconnect = true;
      // El token se guarda como 'irrigo_token' por tokenManager en api.ts
      this.token = token || localStorage.getItem('irrigo_token');

      if (!this.token) {
        console.warn('[WebSocket] No token available, connection may fail');
        this.isConnecting = false;
        reject(new Error('No authentication token available'));
        return;
      }

      try {
        // Append token as query parameter (según documentación WEBSOCKET.md)
        const wsUrl = `${this.url}?token=${encodeURIComponent(this.token)}`;
        console.log('[WebSocket] Connecting to:', wsUrl.replace(/token=[^&]+/, 'token=***'));
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('[WebSocket] Connected');
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.startHeartbeat();
          this.emit('connection.status', { connected: true });
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('[WebSocket] Failed to parse message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('[WebSocket] Error:', error);
          this.isConnecting = false;
          reject(error);
        };

        this.ws.onclose = (event) => {
          console.log('[WebSocket] Disconnected:', event.code, event.reason);
          this.isConnecting = false;
          this.stopHeartbeat();
          this.emit('connection.status', { connected: false });
          
          if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          }
        };
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.shouldReconnect = false;
    this.stopHeartbeat();
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    
    this.emit('connection.status', { connected: false });
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    setTimeout(() => {
      if (this.shouldReconnect) {
        this.connect(this.token || undefined).catch((error) => {
          console.error('[WebSocket] Reconnection failed:', error);
        });
      }
    }, delay);
  }

  /**
   * Start heartbeat to keep connection alive
   */
  private startHeartbeat(): void {
    this.heartbeatInterval = window.setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Every 30 seconds
  }

  /**
   * Stop heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(message: WebSocketMessage): void {
    const { type, payload } = message;
    
    // Log for debugging
    console.log('[WebSocket] Received:', type, payload);
    
    // Emit to subscribers
    this.emit(type, payload);
  }

  /**
   * Emit event to all subscribers
   */
  private emit<T>(type: WebSocketEventType, payload: T): void {
    const listeners = this.eventListeners.get(type);
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(payload);
        } catch (error) {
          console.error(`[WebSocket] Error in listener for ${type}:`, error);
        }
      });
    }
  }

  /**
   * Subscribe to WebSocket event
   */
  on<T>(type: WebSocketEventType, callback: EventCallback<T>): () => void {
    if (!this.eventListeners.has(type)) {
      this.eventListeners.set(type, new Set());
    }
    
    this.eventListeners.get(type)!.add(callback as EventCallback);
    
    // Return unsubscribe function
    return () => {
      this.eventListeners.get(type)?.delete(callback as EventCallback);
    };
  }

  /**
   * Unsubscribe from WebSocket event
   */
  off(type: WebSocketEventType, callback?: EventCallback): void {
    if (callback) {
      this.eventListeners.get(type)?.delete(callback);
    } else {
      this.eventListeners.delete(type);
    }
  }

  /**
   * Send message through WebSocket
   */
  send(message: object): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('[WebSocket] Cannot send message: not connected');
    }
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Get connection state
   */
  getState(): 'connecting' | 'connected' | 'disconnected' {
    if (this.isConnecting) return 'connecting';
    if (this.ws?.readyState === WebSocket.OPEN) return 'connected';
    return 'disconnected';
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();

// Export types
export type {
  WebSocketEventType,
  WebSocketMessage,
  DeviceStatusPayload,
  SensorReadingPayload,
  ValveActionPayload,
  IrrigationEventPayload,
  EventCallback,
};
